// WHAT: Main screen component for timeline/Gantt chart view of roadmap items
// WHY:  Provides visual timeline representation with zoom, filtering, and progress management
// DOMAIN: Admin → Roadmap → Timeline
// DEPENDENCIES: useFilteredRoadmap hook, useRoadmapUpdate hook, Timeline components
// RISKS:
//   - NOTE: Progress calculation logic is complex - may not match user expectations
//   - NOTE: Automatic status sync (100% → done) may override user intentions
//   - NOTE: Date calculations may have timezone issues
//   - NOTE: Uses browser alert for errors - not user-friendly

import { useState, useMemo } from "react";

import { Container, Stack, Modal, Button, Slider } from "@/components/ui";

import { useRoadmapContext } from "../../context/useRoadmapContext";
import { useFilteredRoadmap } from "../../hooks";
import { useRoadmapUpdate } from "../../hooks/useRoadmapUpdate";
import { type Roadmap_Item } from "../../types/roadmap-types";
import { TimelineHeader, TimelineControls, TimelineGrid, TimelineStats } from "./components";

/**
 * Calculate score value from string representation
 *
 * @param score - Score string value
 *
 * @returns {number} Numeric score value
 *
 */
const getScoreValue = (score?: string): number => {
  switch (score) {
    case "low": {
      return 1;
    }
    case "medium": {
      return 2;
    }
    case "high": {
      return 3;
    }
    default: {
      return 0;
    }
  }
};

/**
 * Calculate estimated duration based on effort and priority
 *
 * @param effortEstimate - Effort estimate value
 *
 * @param priority - Priority value
 *
 * @returns {number} Estimated duration in days
 *
 */
const calculateEstimatedDuration = (effortEstimate?: string, priority?: string): number => {
  if (effortEstimate === "high") {
    return priority === "high" ? 10 : 14;
  }
  if (effortEstimate === "medium") {
    return priority === "high" ? 5 : 7;
  }
  return priority === "high" ? 2 : 3;
};

/**
 * WHAT:
 *   Main screen component that displays roadmap items in a Gantt chart-style timeline
 *   with zoom controls, module filtering, and progress management.
 *
 * WHY:
 *   Provides a visual timeline representation of roadmap items, making it easy to see
 *   item durations, overlaps, and progress over time.
 *
 * BEHAVIOR:
 *   - Calculates timeline data from roadmap items with estimated durations
 *   - Supports zoom levels (month/week/day views)
 *   - Filters items by module
 *   - Calculates statistics (totals, status counts, progress, timing)
 *   - Provides progress update modal with automatic status sync
 *   - Handles navigation (previous/next month, today)
 *
 * RISKS:
 *   - NOTE: Progress calculation is complex - may not match user expectations
 *   - NOTE: Automatic status sync (100% → done) may override user intentions
 *   - NOTE: Date calculations use local timezone - may cause issues with UTC dates
 *   - NOTE: Uses browser alert for errors - replace with toast/notification
 *   - NOTE: Duration estimation based on effort/priority may not be accurate
 *
 * @returns {JSX.Element} Timeline screen component
 *
 */
// Reason: Exported component used in routes/index.tsx outside this domain directory

export const TimelineScreen = () => {
  const { items: allItems } = useRoadmapContext();
  const filteredItems = useFilteredRoadmap();
  const updateItem = useRoadmapUpdate();

  // Use filteredItems for timeline display, but allItems for statistics
  const itemsForTimeline = filteredItems.length > 0 ? filteredItems : allItems;
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [zoomLevel, setZoomLevel] = useState(1); // 1 = month, 2 = week, 3 = day
  const [selectedModule, setSelectedModule] = useState<string>("all");
  const [selectedItem, setSelectedItem] = useState<null | Roadmap_Item>(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [manualProgress, setManualProgress] = useState<number>(0);

  // Calculate timeline data with real meaningful information
  const timelineData = useMemo(() => {
    const now = new Date();
    const timelineItems = itemsForTimeline.map((item) => {
      // Create realistic timeline data based on item properties

      // Calculate duration based on effort and priority
      const estimatedDuration = calculateEstimatedDuration(item.effort_estimate, item.priority);

      // Use real dates from database - created_at as start date
      const startDate = new Date(item.created_at);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + estimatedDuration);

      // Use real progress from database, fallback to calculated progress
      let finalProgress = item.progress ?? 0;

      // If no real progress is set, calculate based on status and time
      if (finalProgress === 0) {
        if (item.status === "done") {
          finalProgress = 100; // Done items always 100%
        } else if (item.status === "in_progress") {
          const daysSinceStart = Math.floor(
            (now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
          );
          finalProgress = Math.min(Math.max((daysSinceStart / estimatedDuration) * 100, 10), 90);
        } else {
          const daysUntilStart = Math.floor(
            (startDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
          );
          finalProgress = daysUntilStart <= 0 ? 5 : 0; // 5% if overdue, 0% if not started
        }
      }

      // Override progress to 100% if status is done (regardless of stored progress)
      if (item.status === "done") {
        finalProgress = 100;
      }

      // Calculate ROI for timeline
      const impact = getScoreValue(item.impact_score);
      const effort = getScoreValue(item.effort_estimate);
      const roi = effort > 0 ? impact / effort : impact;

      return {
        dependencies: [],
        effort: item.effort_estimate ?? "medium",
        endDate,
        id: item.id,
        impact: item.impact_score ?? "medium",
        module: item.module,
        originalItem: item, // Store original item for click handler
        priority: item.priority ?? "medium",
        progress: finalProgress,
        roi,
        startDate,
        status: item.status,
        title: item.title,
      };
    });

    return timelineItems;
  }, [itemsForTimeline]);

  // Filter timeline data by selected module
  const filteredTimelineData = useMemo(() => {
    if (selectedModule === "all") {
      return timelineData;
    }
    return timelineData.filter((item) => item.module === selectedModule);
  }, [timelineData, selectedModule]);

  // Get unique modules for filter
  const modules = useMemo(() => {
    const uniqueModules = [...new Set(timelineData.map((item) => item.module))];
    // Reason: toSorted() is preferred but TypeScript may not recognize it in all versions - using sort() on copy instead
    // eslint-disable-next-line unicorn/no-array-sort -- Reason: toSorted() not recognized by TypeScript
    return [...uniqueModules].sort((a, b) => a.localeCompare(b));
  }, [timelineData]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalItems = filteredTimelineData.length;
    const completedItems = filteredTimelineData.filter((item) => item.status === "done").length;
    const inProgressItems = filteredTimelineData.filter(
      (item) => item.status === "in_progress",
    ).length;
    const plannedItems = filteredTimelineData.filter((item) => item.status === "open").length;
    const blockedItems = filteredTimelineData.filter((item) => item.status === "rejected").length;
    const averageProgress =
      totalItems > 0
        ? filteredTimelineData.reduce((sum, item) => sum + item.progress, 0) / totalItems
        : 0;

    const now = new Date();
    const onTimeItems = filteredTimelineData.filter((item) => {
      if (item.status === "done") {
        return true;
      }
      if (item.status === "in_progress") {
        return item.endDate >= now;
      }
      return item.startDate >= now;
    }).length;

    const overdueItems = filteredTimelineData.filter((item) => {
      if (item.status === "done") {
        return false;
      }
      return item.endDate < now;
    }).length;

    return {
      averageProgress,
      blockedItems,
      completedItems,
      inProgressItems,
      onTimeItems,
      overdueItems,
      plannedItems,
      totalItems,
    };
  }, [filteredTimelineData]);

  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentMonth((previous) => new Date(previous.getFullYear(), previous.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth((previous) => new Date(previous.getFullYear(), previous.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const zoomIn = () => {
    setZoomLevel((previous) => Math.min(3, previous + 1));
  };

  const zoomOut = () => {
    setZoomLevel((previous) => Math.max(1, previous - 1));
  };

  const handleItemClick = (item: Roadmap_Item) => {
    setSelectedItem(item);
    setManualProgress(item.progress ?? 0);
    setShowProgressModal(true);
  };

  const handleProgressUpdate = async () => {
    if (!selectedItem) {
      return;
    }

    try {
      // Automatisch Status auf "done" setzen wenn Progress auf 100% gesetzt wird
      const updatedItem = {
        ...selectedItem,
        progress: manualProgress,
        // Wenn Progress 100% ist, Status auf "done" setzen (außer Status ist bereits "rejected")
        status:
          manualProgress === 100 && selectedItem.status !== "rejected"
            ? ("done" as const)
            : selectedItem.status,
      };

      await updateItem(updatedItem);
      setShowProgressModal(false);
      setSelectedItem(null);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : JSON.stringify(error, null, 2);
      alert(`Progress update error: ${message}`);
    }
  };

  return (
    <Container className="text-white" maxWidth="full" padding="lg">
      <Stack direction="col" gap={6}>
        {/* Header */}
        <TimelineHeader />

        {/* Timeline Controls */}
        <TimelineControls
          currentMonth={currentMonth}
          modules={modules}
          onModuleChange={setSelectedModule}
          onNextMonth={goToNextMonth}
          onPreviousMonth={goToPreviousMonth}
          onToday={goToToday}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          selectedModule={selectedModule}
          zoomLevel={zoomLevel}
        />

        {/* Statistics */}
        <TimelineStats {...stats} />

        {/* Timeline Grid */}
        <TimelineGrid
          currentMonth={currentMonth}
          onItemClick={handleItemClick}
          timelineData={filteredTimelineData}
          zoomLevel={zoomLevel}
        />

        {/* Progress Modal */}
        <Modal
          isOpen={showProgressModal}
          onClose={() => {
            setShowProgressModal(false);
          }}
          size="sm"
          title={`Update Progress: ${selectedItem?.title ?? ""}`}
        >
          <div className="space-y-6">
            <div>
              <label className="block text-sm text-slate-300 mb-3">Progress (%)</label>
              <Slider
                className="mb-3"
                max={100}
                min={0}
                onChange={(value) => {
                  setManualProgress(value);
                }}
                step={1}
                value={manualProgress}
              />
              <div className="text-center text-white font-medium text-lg">{manualProgress}%</div>
            </div>
            <div className="flex gap-3">
              <Button
                className="flex-1"
                onClick={() => {
                  void handleProgressUpdate();
                }}
                variant="primary"
              >
                Update
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  setShowProgressModal(false);
                }}
                variant="ghost"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      </Stack>
    </Container>
  );
};
